import { useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Mail, Phone, Facebook, Instagram, Youtube, Music, Sparkles, Wine, PartyPopper, Clock, Users } from 'lucide-react';

const Home = () => {
  const [activeMonth, setActiveMonth] = useState('february');

  const [currentGalleryMonth, setCurrentGalleryMonth] = useState(0);

  // Photo gallery - previous year's events
  const galleryMonths = [
    {
      month: 'February 2025',
      event: 'Pride Night & Drag Spectacular',
      photos: [
        'https://images.pexels.com/photos/1157557/pexels-photo-1157557.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/2747449/pexels-photo-2747449.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/2608517/pexels-photo-2608517.jpeg?auto=compress&cs=tinysrgb&w=800'
      ]
    },
    {
      month: 'January 2025',
      event: 'New Year Celebration',
      photos: [
        'https://images.pexels.com/photos/3171770/pexels-photo-3171770.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/2263436/pexels-photo-2263436.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/1047442/pexels-photo-1047442.jpeg?auto=compress&cs=tinysrgb&w=800'
      ]
    },
    {
      month: 'December 2024',
      event: 'Holiday Extravaganza',
      photos: [
        'https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/1117493/pexels-photo-1117493.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/1024359/pexels-photo-1024359.jpeg?auto=compress&cs=tinysrgb&w=800'
      ]
    }
  ];

  // Video testimonials data
  const videoTestimonials = [
    { name: 'Marcus R.', role: 'Regular since 2023', thumbnail: 'https://images.pexels.com/photos/1466844/pexels-photo-1466844.jpeg?auto=compress&cs=tinysrgb&w=400', quote: 'This is where I found my chosen family' },
    { name: 'Sam T.', role: 'Weekend Warrior', thumbnail: 'https://images.pexels.com/photos/1845534/pexels-photo-1845534.jpeg?auto=compress&cs=tinysrgb&w=400', quote: 'Every Saturday is pure magic here' },
    { name: 'Jordan K.', role: 'Drag Night Devotee', thumbnail: 'https://images.pexels.com/photos/1391498/pexels-photo-1391498.jpeg?auto=compress&cs=tinysrgb&w=400', quote: 'The energy is unmatched anywhere' },
    { name: 'Taylor M.', role: 'First Timer Turned Regular', thumbnail: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400', quote: 'I was nervous at first, now I can\'t stay away' }
  ];

  const drinkSpecials = [
    { name: 'Rainbow Martini', price: '$8', description: 'Vodka, blue curaçao, and pride', image: 'https://images.pexels.com/photos/4753648/pexels-photo-4753648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    { name: 'Pride Punch', price: '$10', description: 'Tropical fruits with a kick', image: 'https://images.pexels.com/photos/605408/pexels-photo-605408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    { name: 'Unicorn Tears', price: '$12', description: 'Magical glitter cocktail', image: 'https://images.pexels.com/photos/4753648/pexels-photo-4753648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    { name: 'Love is Love', price: '$9', description: 'Champagne & strawberry bliss', image: 'https://images.pexels.com/photos/605408/pexels-photo-605408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' }
  ];

  const weeklyEvents = [
    { day: 'Wednesday', title: 'Trivia Night', time: '8:00 PM', icon: Sparkles, color: '#FF1493' },
    { day: 'Thursday', title: 'Karaoke Extravaganza', time: '9:00 PM', icon: Music, color: '#00FFFF' },
    { day: 'Friday', title: 'DJ Night', time: '10:00 PM', icon: Music, color: '#FFD700' },
    { day: 'Saturday', title: 'Drag Show Spectacular', time: '10:00 PM', icon: Sparkles, color: '#FF4500' },
    { day: 'Sunday', title: 'Open Mic & Chill', time: '7:00 PM', icon: Music, color: '#FF1493' }
  ];

  const monthlyEvents = {
    february: [
      { date: '7', title: 'First Friday Dance Party', type: 'DJ', time: '10 PM' },
      { date: '14', title: 'Valentine\'s Love Ball', type: 'Drag', time: '9 PM' },
      { date: '21', title: 'Indie Night Live', type: 'Live Music', time: '8 PM' },
      { date: '28', title: 'Throwback Thursday 90s', type: 'DJ', time: '10 PM' }
    ],
    march: [
      { date: '7', title: 'First Friday Dance Party', type: 'DJ', time: '10 PM' },
      { date: '15', title: 'St. Patrick\'s Pride Bash', type: 'Drag', time: '9 PM' },
      { date: '21', title: 'Spring Fling Concert', type: 'Live Music', time: '8 PM' },
      { date: '28', title: 'Neon Glow Party', type: 'DJ', time: '10 PM' }
    ],
    april: [
      { date: '4', title: 'First Friday Dance Party', type: 'DJ', time: '10 PM' },
      { date: '12', title: 'Drag Brunch Spectacular', type: 'Drag', time: '12 PM' },
      { date: '19', title: 'Acoustic Sessions', type: 'Live Music', time: '7 PM' },
      { date: '26', title: 'Disco Inferno', type: 'DJ', time: '10 PM' }
    ]
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'DJ': return 'bg-[#00FFFF]/20 text-[#00FFFF] border-[#00FFFF]/40';
      case 'Drag': return 'bg-[#FF1493]/20 text-[#FF1493] border-[#FF1493]/40';
      case 'Live Music': return 'bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/40';
      default: return 'bg-white/10 text-white border-white/20';
    }
  };

  return (
    <div className="min-h-screen bg-[#090412] text-white">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 w-full z-50 glass-strong border-b border-white/10"
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <motion.div 
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <Sparkles className="w-8 h-8 text-[#FF1493]" />
              <h1 className="text-2xl md:text-3xl font-black font-['Outfit'] tracking-tighter" data-testid="site-logo">
                GOOD JUDY'S
              </h1>
            </motion.div>
            <div className="hidden md:flex items-center gap-8 font-['Outfit'] font-medium">
              <a href="#about" className="hover:text-[#FF1493] transition-colors" data-testid="nav-about">About</a>
              <a href="#drinks" className="hover:text-[#00FFFF] transition-colors" data-testid="nav-drinks">Drinks</a>
              <a href="#events" className="hover:text-[#FFD700] transition-colors" data-testid="nav-events">Events</a>
              <a href="#contact" className="hover:text-[#FF4500] transition-colors" data-testid="nav-contact">Contact</a>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/6984986/pexels-photo-6984986.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920" 
            alt="Vibrant pride celebration" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 hero-gradient"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#00FFFF] mb-6 font-['Space_Mono']" data-testid="hero-tagline">
              Grand Junction's Only Inclusive Bar & Club
            </p>
            <h1 className="font-['Outfit'] font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter mb-6" data-testid="hero-title">
              Where Everyone
              <br />
              <span className="text-gradient-rainbow">Belongs</span>
            </h1>
            <p className="text-lg md:text-xl text-[#A39BB0] max-w-2xl mx-auto mb-12 leading-relaxed" data-testid="hero-description">
              A vibrant, inclusive space serving the LGBTQ+ community and allies in the heart of downtown Grand Junction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.a
                href="#events"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary text-white font-bold rounded-full px-8 py-4 text-lg font-['Outfit']"
                data-testid="hero-cta-primary"
              >
                View Events
              </motion.a>
              <motion.a
                href="#contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary glass border border-white/20 text-white rounded-full px-8 py-4 text-lg font-['Outfit']"
                data-testid="hero-cta-secondary"
              >
                Get Directions
              </motion.a>
            </div>
          </motion.div>
        </div>

        {/* Animated Marquee */}
        <div className="absolute bottom-20 left-0 w-full overflow-hidden opacity-5">
          <div className="flex whitespace-nowrap animate-marquee font-['Outfit'] font-black text-8xl tracking-tighter">
            <span className="px-12">PRIDE • LOVE • UNITY • PRIDE • LOVE • UNITY • </span>
            <span className="px-12">PRIDE • LOVE • UNITY • PRIDE • LOVE • UNITY • </span>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-['Outfit'] font-black text-4xl md:text-6xl tracking-tight mb-6" data-testid="about-heading">
                More Than Just
                <br />
                <span className="text-[#FF1493]">A Night Out</span>
              </h2>
              <p className="text-lg text-[#A39BB0] leading-relaxed mb-6" data-testid="about-description">
                Welcome to Good Judy's, where diversity, inclusivity, and equality aren't just words—they're our foundation. We're a vibrant space that serves the LGBTQ+ community and our allies in the heart of Grand Junction.
              </p>
              <p className="text-lg text-[#A39BB0] leading-relaxed mb-8">
                From drag shows to live music, DJ sets to themed parties, we create an environment where everyone can feel safe, comfortable, and free to express themselves. Come as you are, leave as family.
              </p>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#FF1493]/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#FF1493]" />
                  </div>
                  <div>
                    <p className="font-bold font-['Outfit']">Inclusive</p>
                    <p className="text-sm text-[#A39BB0]">All Welcome</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#00FFFF]/20 flex items-center justify-center">
                    <PartyPopper className="w-6 h-6 text-[#00FFFF]" />
                  </div>
                  <div>
                    <p className="font-bold font-['Outfit']">Vibrant</p>
                    <p className="text-sm text-[#A39BB0]">Always Fun</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img 
                src="https://images.pexels.com/photos/1612752/pexels-photo-1612752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Pride celebration" 
                className="rounded-2xl w-full h-64 object-cover"
              />
              <img 
                src="https://static.wixstatic.com/media/11062b_47f246c73a0c41f89b088458c4818f48f000.jpg/v1/fill/w_778,h_1080,al_c,q_85,usm_0.33_1.00_0.00,enc_avif,quality_auto/11062b_47f246c73a0c41f89b088458c4818f48f000.jpg" 
                alt="Good Judy's venue" 
                className="rounded-2xl w-full h-64 object-cover mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* First Timer Welcome Package */}
      <section className="py-32 bg-gradient-to-br from-[#00FFFF]/10 via-[#090412] to-[#FF1493]/10 relative overflow-hidden" data-testid="first-timer-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFD700] rounded-full blur-[120px]"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="glass-strong rounded-3xl p-10 md:p-16 border-2 border-[#FFD700]/30 text-center relative overflow-hidden"
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#FF1493] via-[#00FFFF] to-[#FFD700]"></div>
            
            <Sparkles className="w-16 h-16 text-[#FFD700] mx-auto mb-6 animate-pulse" />
            
            <div className="inline-block px-6 py-2 rounded-full bg-[#FFD700]/20 border border-[#FFD700]/40 mb-6">
              <p className="text-sm font-bold text-[#FFD700] tracking-wider">FIRST TIME HERE?</p>
            </div>

            <h2 className="font-['Outfit'] font-black text-4xl md:text-5xl tracking-tight mb-6" data-testid="first-timer-heading">
              Welcome to the <span className="text-gradient-primary">Family</span>
            </h2>
            
            <p className="text-lg md:text-xl text-[#A39BB0] mb-8 leading-relaxed max-w-2xl mx-auto">
              Never been to Good Judy's? We got you! Claim your First Timer Welcome Package and experience what makes us Grand Junction's most inclusive space.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="glass rounded-2xl p-6 border border-[#FF1493]/20">
                <div className="w-16 h-16 rounded-full bg-[#FF1493]/20 flex items-center justify-center mx-auto mb-4">
                  <Wine className="w-8 h-8 text-[#FF1493]" />
                </div>
                <h3 className="font-['Outfit'] font-bold text-xl mb-2">2 Free Well Drinks</h3>
                <p className="text-sm text-[#A39BB0]">Your first two on us</p>
              </div>

              <div className="glass rounded-2xl p-6 border border-[#00FFFF]/20">
                <div className="w-16 h-16 rounded-full bg-[#00FFFF]/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-[#00FFFF]" />
                </div>
                <h3 className="font-['Outfit'] font-bold text-xl mb-2">VIP Access</h3>
                <p className="text-sm text-[#A39BB0]">One night in VIP (when available)</p>
              </div>

              <div className="glass rounded-2xl p-6 border border-[#FFD700]/20">
                <div className="w-16 h-16 rounded-full bg-[#FFD700]/20 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-[#FFD700]" />
                </div>
                <h3 className="font-['Outfit'] font-bold text-xl mb-2">Personal Welcome</h3>
                <p className="text-sm text-[#A39BB0]">Our team will show you around</p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary text-white font-bold rounded-full px-10 py-4 text-lg font-['Outfit'] inline-flex items-center gap-3"
              data-testid="claim-welcome-package-btn"
            >
              Claim Your Welcome Package
              <PartyPopper className="w-5 h-5" />
            </motion.button>

            <p className="text-xs text-[#A39BB0] mt-6">
              Valid for first-time visitors • Must show this page on mobile • Limited to one per person
            </p>
          </motion.div>
        </div>
      </section>

      {/* Good Judy's TV - Live Stream Section */}
      <section className="py-32 relative overflow-hidden" data-testid="live-stream-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-red-500 font-['Space_Mono']" data-testid="live-indicator">
                LIVE NOW
              </p>
            </div>
            <h2 className="font-['Outfit'] font-black text-4xl md:text-6xl tracking-tight mb-6" data-testid="live-stream-heading">
              Good Judy's <span className="text-gradient-primary">TV</span>
            </h2>
            <p className="text-lg text-[#A39BB0] max-w-2xl mx-auto">
              Tune in to the club vibes from anywhere. Experience the energy live!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            {/* TV Frame */}
            <div className="relative glass-strong rounded-3xl p-8 border-2 border-white/20" data-testid="tv-frame">
              {/* Screen */}
              <div className="relative aspect-video bg-gradient-to-br from-[#FF1493]/20 via-[#120B21] to-[#00FFFF]/20 rounded-2xl overflow-hidden border border-white/10">
                {/* Video placeholder with animated gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF1493]/30 via-[#9D4EDD]/30 to-[#00FFFF]/30 animate-pulse"></div>
                
                {/* Animated equalizer bars in background */}
                <div className="absolute inset-0 flex items-end justify-center gap-2 p-12 opacity-30">
                  {[...Array(12)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-8 bg-gradient-to-t from-[#FF1493] to-[#00FFFF] rounded-t-lg"
                      animate={{
                        height: ['20%', '80%', '40%', '90%', '30%'],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        delay: i * 0.1,
                        ease: 'easeInOut',
                      }}
                    />
                  ))}
                </div>

                {/* Content overlay */}
                <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
                  <Music className="w-20 h-20 text-white mb-6 animate-pulse" />
                  <h3 className="font-['Outfit'] font-bold text-3xl mb-4" data-testid="now-playing-title">
                    Now Playing
                  </h3>
                  <div className="glass-strong rounded-2xl px-8 py-4 mb-6 max-w-md">
                    <p className="text-2xl font-bold text-[#00FFFF] mb-1" data-testid="current-track">
                      Electric Vibes Mix
                    </p>
                    <p className="text-sm text-[#A39BB0]" data-testid="current-dj">
                      DJ Sparkle • Live from Grand Junction
                    </p>
                  </div>
                  
                  {/* Play button */}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-20 h-20 rounded-full bg-gradient-to-r from-[#FF1493] to-[#FF4500] flex items-center justify-center neon-glow-pink cursor-pointer"
                    data-testid="play-stream-btn"
                  >
                    <div className="w-0 h-0 border-t-[12px] border-t-transparent border-l-[20px] border-l-white border-b-[12px] border-b-transparent ml-1"></div>
                  </motion.button>
                  
                  <p className="text-xs text-[#A39BB0] mt-4">
                    Click to start streaming • Audio & Video available
                  </p>
                </div>

                {/* Live badge */}
                <div className="absolute top-4 left-4 flex items-center gap-2 glass-strong rounded-full px-4 py-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-bold tracking-wider">LIVE</span>
                </div>

                {/* Viewer count */}
                <div className="absolute top-4 right-4 glass-strong rounded-full px-4 py-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#00FFFF]" />
                  <span className="text-sm font-bold">127</span>
                </div>
              </div>

              {/* Controls bar */}
              <div className="mt-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button className="w-12 h-12 rounded-full glass border border-white/20 flex items-center justify-center hover:border-[#FF1493] transition-all" data-testid="audio-mode-btn">
                    <Music className="w-5 h-5" />
                  </button>
                  <button className="w-12 h-12 rounded-full glass border border-white/20 flex items-center justify-center hover:border-[#00FFFF] transition-all" data-testid="video-mode-btn">
                    <PartyPopper className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="flex-1 flex items-center gap-3">
                  <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-[#FF1493] to-[#00FFFF]"
                      animate={{ width: ['0%', '100%'] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    />
                  </div>
                  <span className="text-sm text-[#A39BB0] font-['Space_Mono']">LIVE</span>
                </div>

                <div className="flex items-center gap-2">
                  <button className="px-6 py-3 rounded-full glass border border-white/20 hover:border-[#FFD700] transition-all font-['Outfit'] font-bold text-sm" data-testid="share-stream-btn">
                    Share
                  </button>
                </div>
              </div>
            </div>

            {/* Feature highlights */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="glass rounded-2xl p-6 text-center border border-white/10">
                <div className="w-14 h-14 rounded-full bg-[#FF1493]/20 flex items-center justify-center mx-auto mb-4">
                  <Music className="w-7 h-7 text-[#FF1493]" />
                </div>
                <h4 className="font-['Outfit'] font-bold mb-2">Audio Stream</h4>
                <p className="text-sm text-[#A39BB0]">Crystal clear club audio feed</p>
              </div>
              <div className="glass rounded-2xl p-6 text-center border border-white/10">
                <div className="w-14 h-14 rounded-full bg-[#00FFFF]/20 flex items-center justify-center mx-auto mb-4">
                  <PartyPopper className="w-7 h-7 text-[#00FFFF]" />
                </div>
                <h4 className="font-['Outfit'] font-bold mb-2">Live Video</h4>
                <p className="text-sm text-[#A39BB0]">Watch the party unfold</p>
              </div>
              <div className="glass rounded-2xl p-6 text-center border border-white/10">
                <div className="w-14 h-14 rounded-full bg-[#FFD700]/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-[#FFD700]" />
                </div>
                <h4 className="font-['Outfit'] font-bold mb-2">24/7 Access</h4>
                <p className="text-sm text-[#A39BB0]">Stream anytime, anywhere</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contest Section - Join the Family */}
      <section className="py-32 bg-gradient-to-b from-[#090412] via-[#FF1493]/10 to-[#090412] relative overflow-hidden" data-testid="contest-section">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF1493] rounded-full blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-[#00FFFF] rounded-full blur-[100px]"></div>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#FFD700] mb-4 font-['Space_Mono']" data-testid="contest-overline">
              Monthly Giveaway
            </p>
            <h2 className="font-['Outfit'] font-black text-4xl md:text-6xl tracking-tight mb-6" data-testid="contest-heading">
              Join the <span className="text-gradient-rainbow">Family</span>
            </h2>
            <p className="text-lg md:text-xl text-[#A39BB0] max-w-3xl mx-auto leading-relaxed">
              This isn't just a club—it's YOUR space. Enter for a chance to win VIP access for a month, exclusive merch, and become part of the Good Judy's family!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left side - Prize showcase */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="glass-strong rounded-3xl p-8 border-2 border-[#FF1493]/30">
                <Sparkles className="w-12 h-12 text-[#FFD700] mb-4" />
                <h3 className="font-['Outfit'] font-black text-3xl mb-4">Grand Prize</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#FF1493]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-[#FF1493] text-sm">✓</span>
                    </div>
                    <div>
                      <p className="font-bold text-lg">VIP Access - 1 Month</p>
                      <p className="text-sm text-[#A39BB0]">Skip lines, reserved seating, exclusive entry</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00FFFF]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-[#00FFFF] text-sm">✓</span>
                    </div>
                    <div>
                      <p className="font-bold text-lg">Exclusive Merch Pack</p>
                      <p className="text-sm text-[#A39BB0]">Good Judy's swag you can't buy</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#FFD700]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-[#FFD700] text-sm">✓</span>
                    </div>
                    <div>
                      <p className="font-bold text-lg">Free Drink Vouchers</p>
                      <p className="text-sm text-[#A39BB0]">4 signature cocktails on us</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#FF4500]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-[#FF4500] text-sm">✓</span>
                    </div>
                    <div>
                      <p className="font-bold text-lg">Featured on Good Judy's TV</p>
                      <p className="text-sm text-[#A39BB0]">Be part of our community spotlight</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-6 border border-white/10">
                <p className="text-sm text-[#A39BB0] mb-3">💡 Bonus entries:</p>
                <div className="space-y-2 text-sm">
                  <p>• Share on social media: <span className="text-[#00FFFF] font-bold">+3 entries</span></p>
                  <p>• Tag 3 friends: <span className="text-[#FF1493] font-bold">+2 entries</span></p>
                  <p>• Follow @good_judys: <span className="text-[#FFD700] font-bold">+2 entries</span></p>
                </div>
              </div>
            </motion.div>

            {/* Right side - Entry form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="glass-strong rounded-3xl p-8 border-2 border-white/20">
                <div className="mb-6">
                  <h3 className="font-['Outfit'] font-bold text-2xl mb-2">Enter to Win</h3>
                  <p className="text-sm text-[#A39BB0]">Drawing happens at the end of each month</p>
                </div>

                <form className="space-y-4" data-testid="contest-form">
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="contest-name">Full Name *</label>
                    <input
                      id="contest-name"
                      type="text"
                      placeholder="Your name"
                      className="w-full px-4 py-3 rounded-xl glass border border-white/20 bg-white/5 focus:border-[#FF1493] focus:outline-none transition-all"
                      data-testid="contest-name-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="contest-email">Email Address *</label>
                    <input
                      id="contest-email"
                      type="email"
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-xl glass border border-white/20 bg-white/5 focus:border-[#00FFFF] focus:outline-none transition-all"
                      data-testid="contest-email-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="contest-instagram">Instagram Handle (Optional)</label>
                    <input
                      id="contest-instagram"
                      type="text"
                      placeholder="@yourhandle"
                      className="w-full px-4 py-3 rounded-xl glass border border-white/20 bg-white/5 focus:border-[#FFD700] focus:outline-none transition-all"
                      data-testid="contest-instagram-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="contest-story">Why is Good Judy's YOUR place? (Optional)</label>
                    <textarea
                      id="contest-story"
                      rows="3"
                      placeholder="Share your story..."
                      className="w-full px-4 py-3 rounded-xl glass border border-white/20 bg-white/5 focus:border-[#FF1493] focus:outline-none transition-all resize-none"
                      data-testid="contest-story-input"
                    ></textarea>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="contest-terms"
                      className="mt-1 w-5 h-5 rounded border-white/20"
                      data-testid="contest-terms-checkbox"
                      required
                    />
                    <label htmlFor="contest-terms" className="text-xs text-[#A39BB0]">
                      I agree to receive updates about events and future contests. You can unsubscribe anytime. We respect your privacy.
                    </label>
                  </div>

                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full btn-primary text-white font-bold rounded-full py-4 text-lg font-['Outfit']"
                    data-testid="contest-submit-btn"
                  >
                    Enter Contest
                  </motion.button>

                  <p className="text-xs text-center text-[#A39BB0]">
                    Contest ends on the last day of each month • Winner announced on social media
                  </p>
                </form>
              </div>

              {/* Social share buttons */}
              <div className="mt-6 glass rounded-2xl p-6 border border-white/10">
                <p className="text-sm font-bold mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FFD700]" />
                  Get 3 bonus entries by sharing:
                </p>
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 py-3 rounded-full bg-[#FF1493]/20 border border-[#FF1493]/40 hover:bg-[#FF1493]/30 transition-all font-bold text-sm"
                    data-testid="share-facebook-btn"
                  >
                    <Facebook className="w-4 h-4 inline mr-2" />
                    Share
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 py-3 rounded-full bg-[#00FFFF]/20 border border-[#00FFFF]/40 hover:bg-[#00FFFF]/30 transition-all font-bold text-sm"
                    data-testid="share-instagram-btn"
                  >
                    <Instagram className="w-4 h-4 inline mr-2" />
                    Share
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Community message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="glass rounded-2xl p-8 border border-white/10 max-w-3xl mx-auto">
              <Users className="w-12 h-12 text-[#FF1493] mx-auto mb-4" />
              <h4 className="font-['Outfit'] font-bold text-2xl mb-3">
                Everyone Belongs Here
              </h4>
              <p className="text-[#A39BB0] leading-relaxed">
                Good Judy's isn't just about the contest—it's about building a community where everyone feels at home. 
                Whether you win or not, you're already part of our family. Come through, bring your friends, 
                and let's make memories together. This is YOUR space, YOUR club, YOUR Good Judy's.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Drink Specials Section */}
      <section id="drinks" className="py-32 bg-[#120B21]/50 overflow-hidden" data-testid="drinks-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#FFD700] mb-4 font-['Space_Mono']" data-testid="drinks-overline">
              Signature Cocktails
            </p>
            <h2 className="font-['Outfit'] font-black text-4xl md:text-6xl tracking-tight mb-6" data-testid="drinks-heading">
              Drink <span className="text-[#00FFFF]">Specials</span>
            </h2>
            <p className="text-[#A39BB0] text-sm">← Scroll to explore →</p>
          </motion.div>

          {/* Horizontal Scrolling Menu */}
          <div className="relative -mx-6 md:-mx-12">
            <div className="flex gap-6 overflow-x-auto pb-6 px-6 md:px-12 snap-x snap-mandatory scrollbar-hide">
              {drinkSpecials.map((drink, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="drink-card glass rounded-2xl overflow-hidden flex-shrink-0 w-80 snap-center group"
                  data-testid={`drink-card-${index}`}
                >
                  <div className="relative h-64">
                    <img 
                      src={drink.image} 
                      alt={drink.name} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#090412] via-[#090412]/60 to-transparent"></div>
                  </div>
                  <div className="p-6 bg-gradient-to-b from-[#120B21] to-[#090412]">
                    <div className="flex items-start justify-between mb-3">
                      <Wine className="w-8 h-8 text-[#FF1493]" />
                      <span className="font-['Outfit'] font-black text-3xl text-[#FFD700]" data-testid={`drink-price-${index}`}>{drink.price}</span>
                    </div>
                    <h3 className="font-['Outfit'] font-bold text-2xl mb-2" data-testid={`drink-name-${index}`}>{drink.name}</h3>
                    <p className="text-base text-[#A39BB0]">{drink.description}</p>
                  </div>
                </motion.div>
              ))}
              {/* Add more drinks for better scrolling experience */}
              {drinkSpecials.slice(0, 2).map((drink, index) => (
                <motion.div
                  key={`extra-${index}`}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: (drinkSpecials.length + index) * 0.1 }}
                  viewport={{ once: true }}
                  className="drink-card glass rounded-2xl overflow-hidden flex-shrink-0 w-80 snap-center group"
                  data-testid={`drink-card-extra-${index}`}
                >
                  <div className="relative h-64">
                    <img 
                      src={drink.image} 
                      alt={drink.name} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#090412] via-[#090412]/60 to-transparent"></div>
                  </div>
                  <div className="p-6 bg-gradient-to-b from-[#120B21] to-[#090412]">
                    <div className="flex items-start justify-between mb-3">
                      <Wine className="w-8 h-8 text-[#FF1493]" />
                      <span className="font-['Outfit'] font-black text-3xl text-[#FFD700]">{drink.price}</span>
                    </div>
                    <h3 className="font-['Outfit'] font-bold text-2xl mb-2">{drink.name}</h3>
                    <p className="text-base text-[#A39BB0]">{drink.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="py-32" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Weekly Events */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <div className="text-center mb-16">
              <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#FF4500] mb-4 font-['Space_Mono']" data-testid="weekly-events-overline">
                Every Week
              </p>
              <h2 className="font-['Outfit'] font-black text-4xl md:text-6xl tracking-tight mb-6" data-testid="weekly-events-heading">
                Weekly <span className="text-[#FF1493]">Events</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {weeklyEvents.map((event, index) => {
                const IconComponent = event.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="event-card glass rounded-2xl p-6 border border-white/10"
                    data-testid={`weekly-event-${index}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `${event.color}20`, border: `2px solid ${event.color}40` }}>
                        <IconComponent className="w-7 h-7" style={{ color: event.color }} />
                      </div>
                      <div className="text-right">
                        <p className="text-xs tracking-[0.2em] uppercase text-[#A39BB0] font-['Space_Mono']">{event.day}</p>
                        <p className="text-sm font-bold mt-1 flex items-center gap-1 justify-end">
                          <Clock className="w-4 h-4" />
                          {event.time}
                        </p>
                      </div>
                    </div>
                    <h3 className="font-['Outfit'] font-bold text-xl mb-2" data-testid={`weekly-event-title-${index}`}>{event.title}</h3>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Monthly Calendar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-16">
              <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#00FFFF] mb-4 font-['Space_Mono']" data-testid="monthly-events-overline">
                This Month
              </p>
              <h2 className="font-['Outfit'] font-black text-4xl md:text-6xl tracking-tight mb-6" data-testid="monthly-events-heading">
                Monthly <span className="text-[#FFD700]">Calendar</span>
              </h2>
            </div>

            {/* Month Selector */}
            <div className="flex justify-center gap-4 mb-12">
              {['february', 'march', 'april'].map((month) => (
                <button
                  key={month}
                  onClick={() => setActiveMonth(month)}
                  className={`px-6 py-3 rounded-full font-['Outfit'] font-bold capitalize transition-all ${
                    activeMonth === month 
                      ? 'bg-gradient-to-r from-[#FF1493] to-[#FF4500] text-white neon-glow-pink' 
                      : 'glass border border-white/10 text-[#A39BB0] hover:text-white'
                  }`}
                  data-testid={`month-selector-${month}`}
                >
                  {month}
                </button>
              ))}
            </div>

            {/* Events Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {monthlyEvents[activeMonth].map((event, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="glass-strong rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all"
                  data-testid={`monthly-event-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#FF1493] to-[#FF4500] flex items-center justify-center">
                      <span className="font-['Outfit'] font-black text-3xl">{event.date}</span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getEventTypeColor(event.type)}`} data-testid={`event-type-${index}`}>
                      {event.type}
                    </span>
                  </div>
                  <h3 className="font-['Outfit'] font-bold text-lg mb-2" data-testid={`monthly-event-title-${index}`}>{event.title}</h3>
                  <p className="text-sm text-[#A39BB0] flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {event.time}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery - Last Year's Events */}
      <section className="py-32 bg-[#120B21]/30" data-testid="photo-gallery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#FF1493] mb-4 font-['Space_Mono']" data-testid="gallery-overline">
              Memories Made Here
            </p>
            <h2 className="font-['Outfit'] font-black text-4xl md:text-6xl tracking-tight mb-6" data-testid="gallery-heading">
              See What You <span className="text-[#00FFFF]">Missed</span>
            </h2>
            <p className="text-lg text-[#A39BB0] max-w-2xl mx-auto mb-8">
              Last year's magic, this year's inspiration. Don't miss out on making memories!
            </p>

            {/* Month selector */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {galleryMonths.map((gallery, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentGalleryMonth(index)}
                  className={`px-6 py-3 rounded-full font-['Outfit'] font-bold transition-all ${
                    currentGalleryMonth === index
                      ? 'bg-gradient-to-r from-[#FF1493] to-[#00FFFF] text-white'
                      : 'glass border border-white/10 text-[#A39BB0] hover:text-white hover:border-white/20'
                  }`}
                  data-testid={`gallery-month-${index}`}
                >
                  {gallery.month}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Current gallery display */}
          <motion.div
            key={currentGalleryMonth}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="glass-strong rounded-2xl p-6 mb-8 text-center border border-white/10">
              <h3 className="font-['Outfit'] font-bold text-2xl mb-2" data-testid="current-event-name">
                {galleryMonths[currentGalleryMonth].event}
              </h3>
              <p className="text-[#A39BB0]">{galleryMonths[currentGalleryMonth].month}</p>
            </div>

            {/* Photo grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {galleryMonths[currentGalleryMonth].photos.map((photo, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="relative group cursor-pointer overflow-hidden rounded-2xl aspect-square"
                  data-testid={`gallery-photo-${index}`}
                >
                  <img
                    src={photo}
                    alt={`Event memory ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#090412] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                    <span className="text-sm font-bold">View Full Size</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Call to action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <div className="glass rounded-2xl p-8 inline-block border border-white/10">
              <p className="text-lg mb-4">Want to be in next year's gallery?</p>
              <motion.a
                href="#events"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary text-white font-bold rounded-full px-8 py-3 inline-flex items-center gap-2 font-['Outfit']"
                data-testid="gallery-cta-btn"
              >
                Check Upcoming Events
                <Calendar className="w-5 h-5" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Testimonials */}
      <section className="py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#00FFFF] mb-4 font-['Space_Mono']" data-testid="testimonials-overline">
              Real Stories, Real People
            </p>
            <h2 className="font-['Outfit'] font-black text-4xl md:text-6xl tracking-tight mb-6" data-testid="testimonials-heading">
              Hear From <span className="text-gradient-primary">Our Family</span>
            </h2>
            <p className="text-lg text-[#A39BB0] max-w-2xl mx-auto">
              Don't just take our word for it. Listen to the voices of those who make Good Judy's their home.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {videoTestimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-strong rounded-2xl overflow-hidden border border-white/10 group cursor-pointer"
                data-testid={`testimonial-${index}`}
              >
                {/* Video thumbnail */}
                <div className="relative aspect-video bg-gradient-to-br from-[#FF1493]/20 to-[#00FFFF]/20 overflow-hidden">
                  <img
                    src={testimonial.thumbnail}
                    alt={testimonial.name}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-300"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="w-20 h-20 rounded-full bg-gradient-to-r from-[#FF1493] to-[#FF4500] flex items-center justify-center neon-glow-pink"
                    >
                      <div className="w-0 h-0 border-t-[15px] border-t-transparent border-l-[25px] border-l-white border-b-[15px] border-b-transparent ml-2"></div>
                    </motion.div>
                  </div>
                  <div className="absolute top-4 right-4 glass-strong rounded-full px-3 py-1 text-xs font-bold">
                    Video
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-lg font-['Outfit'] font-bold mb-2 italic">&ldquo;{testimonial.quote}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF1493] to-[#00FFFF] flex items-center justify-center font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold" data-testid={`testimonial-name-${index}`}>{testimonial.name}</p>
                      <p className="text-sm text-[#A39BB0]">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA to submit testimonial */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <div className="glass-strong rounded-2xl p-8 max-w-2xl mx-auto border border-white/10">
              <h3 className="font-['Outfit'] font-bold text-2xl mb-4">Got a Story to Share?</h3>
              <p className="text-[#A39BB0] mb-6">
                We'd love to hear how Good Judy's has impacted you. Submit your video testimonial and you might be featured!
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary glass border border-white/20 text-white rounded-full px-8 py-3 font-['Outfit'] font-bold"
                data-testid="submit-testimonial-btn"
              >
                Submit Your Story
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-32 bg-gradient-to-r from-[#FF1493]/20 via-[#120B21] to-[#00FFFF]/20" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-16 h-16 text-[#FFD700] mx-auto mb-6" />
            <h2 className="font-['Outfit'] font-black text-4xl md:text-5xl tracking-tight mb-6" data-testid="newsletter-heading">
              Stay in the Loop
            </h2>
            <p className="text-lg text-[#A39BB0] mb-8 leading-relaxed">
              Get the latest news, shows, and events delivered straight to your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" data-testid="newsletter-form">
              <input 
                type="email" 
                placeholder="Enter your email" 
                className="flex-1 px-6 py-4 rounded-full glass border border-white/20 bg-white/5 focus:border-[#FF1493] focus:outline-none transition-all"
                data-testid="newsletter-email-input"
              />
              <motion.button
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary text-white font-bold rounded-full px-8 py-4 font-['Outfit']"
                data-testid="newsletter-submit-btn"
              >
                Subscribe
              </motion.button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer / Contact Section */}
      <footer id="contact" className="py-20 bg-[#090412] border-t border-white/10" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Hours */}
            <div>
              <h3 className="font-['Outfit'] font-bold text-xl mb-4 flex items-center gap-2" data-testid="hours-heading">
                <Clock className="w-5 h-5 text-[#FF1493]" />
                Hours
              </h3>
              <p className="text-[#A39BB0]" data-testid="hours-info">Wednesday - Sunday</p>
              <p className="text-white font-bold">7:00 PM - Close</p>
              <p className="text-sm text-[#A39BB0] mt-2">Brunch coming soon!</p>
            </div>

            {/* Location */}
            <div>
              <h3 className="font-['Outfit'] font-bold text-xl mb-4 flex items-center gap-2" data-testid="location-heading">
                <MapPin className="w-5 h-5 text-[#00FFFF]" />
                Location
              </h3>
              <p className="text-white font-bold" data-testid="location-address">103 N 1st Street</p>
              <p className="text-[#A39BB0]">Grand Junction, CO 81501</p>
              <p className="text-sm text-[#A39BB0] mt-2">Free parking available on site</p>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-['Outfit'] font-bold text-xl mb-4 flex items-center gap-2" data-testid="contact-heading">
                <Mail className="w-5 h-5 text-[#FFD700]" />
                Contact
              </h3>
              <a href="mailto:tea@good-judys.com" className="text-white hover:text-[#FF1493] transition-colors block mb-2" data-testid="contact-email">
                tea@good-judys.com
              </a>
              <a href="tel:970-433-7115" className="text-white hover:text-[#00FFFF] transition-colors flex items-center gap-2" data-testid="contact-phone">
                <Phone className="w-4 h-4" />
                970-433-7115
              </a>
            </div>
          </div>

          {/* Social Links */}
          <div className="flex justify-center gap-6 mb-12">
            <motion.a 
              href="https://www.facebook.com/goodjudysgj" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: 5 }}
              className="w-12 h-12 rounded-full glass border border-white/20 flex items-center justify-center hover:border-[#FF1493] transition-all"
              data-testid="social-facebook"
            >
              <Facebook className="w-5 h-5" />
            </motion.a>
            <motion.a 
              href="https://instagram.com/good_judys" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: -5 }}
              className="w-12 h-12 rounded-full glass border border-white/20 flex items-center justify-center hover:border-[#00FFFF] transition-all"
              data-testid="social-instagram"
            >
              <Instagram className="w-5 h-5" />
            </motion.a>
            <motion.a 
              href="https://www.youtube.com/@goodjudys" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, rotate: 5 }}
              className="w-12 h-12 rounded-full glass border border-white/20 flex items-center justify-center hover:border-[#FFD700] transition-all"
              data-testid="social-youtube"
            >
              <Youtube className="w-5 h-5" />
            </motion.a>
          </div>

          {/* Copyright */}
          <div className="text-center text-[#A39BB0] text-sm">
            <p>&copy; 2026 Good Judy's. All rights reserved.</p>
            <p className="mt-2">Love is Love. Pride is Power. Everyone Belongs.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
